/* ============================================================
   Laley PDP Clean v4 — commerce
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: commerce');
    return;
  }

  // Badge-kanon (SKIMS-runde 2, juli-26): ren sans, aldri mono-aliaset.
  const OUTFIT_BADGE_FONT = '"Inter", "Helvetica Neue", Arial, sans-serif';

  const applyOutfitBadgeTypography = (root = document) => {
    const badges = root.matches?.('.pdp-clean__outfit-card-badge')
      ? [root]
      : Array.from(root.querySelectorAll?.('.pdp-clean__outfit-card-badge') || []);
    badges.forEach((badge) => {
      if (
        badge.style.getPropertyValue('font-family') === OUTFIT_BADGE_FONT
        && badge.style.getPropertyPriority('font-family') === 'important'
      ) return;
      badge.style.setProperty('font-family', OUTFIT_BADGE_FONT, 'important');
    });
  };

  PDP.registerModule('commerce', {
    productTitle() {
      return this.section.querySelector('.pdp-clean__title')?.textContent?.trim() || 'produktet';
    },

    bindOutfitBadgeTypography() {
      const outfit = this.section.querySelector('.pdp-clean__outfit') || this.section;
      let raf = null;
      const schedule = () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          applyOutfitBadgeTypography(outfit);
        });
      };
      schedule();
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            schedule();
            return;
          }
          if (
            mutation.type === 'attributes'
            && mutation.attributeName === 'style'
            && mutation.target?.classList?.contains('pdp-clean__outfit-card-badge')
          ) {
            schedule();
            return;
          }
        }
      });
      observer.observe(outfit, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
      this.unsubs.push(() => {
        if (raf) cancelAnimationFrame(raf);
        observer.disconnect();
      });
    },

    bindOutfitForm(form, bubbles = false) {
      if (!form || form.dataset.pdpOutfitBound === 'true') return;
      form.dataset.pdpOutfitBound = 'true';
      const button = form.querySelector('[data-pdp-outfit-add]');
      const select = form.querySelector('[data-pdp-outfit-select]');
      const idInput = form.querySelector('[data-pdp-outfit-variant-id]') || form.querySelector('input[name="id"]');
      if (!button) return;

      const sync = () => {
        if (!select) {
          button.disabled = !idInput?.value || button.dataset.forceDisabled === 'true';
          return;
        }
        const selected = select.selectedOptions?.[0];
        const value = select.value || '';
        const available = Boolean(value) && selected?.dataset.available !== 'false' && !selected?.disabled;
        if (idInput) idInput.value = available ? value : '';
        button.disabled = !available;
        // Sondre juli-26: knappen er skjult til størrelse er valgt (sparer
        // kort-høyde) — vis den ved gyldig valg, skjul igjen ved tom/utsolgt.
        button.hidden = !value;
      };

      if (select) {
        this.on(select, 'change', sync);
        sync();
      }

      this.on(form, 'submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        sync();
        if (button.disabled) {
          if (select) select.focus();
          return;
        }
        this.addOutfitFormToCart(form, button, sync);
      });
    },

    async addOutfitFormToCart(form, button, sync) {
      const formData = new FormData(form);
      const variantId = String(formData.get('id') || '').trim();
      if (!variantId) {
        button.textContent = 'VELG STØRRELSE';
        sync?.();
        return;
      }

      const cartItems = document.querySelector('cart-items');
      if (cartItems) {
        const cleanup = () => {
          button.removeAttribute('aria-busy');
          sync?.();
        };
        button.setAttribute('aria-busy', 'true');
        document.addEventListener('theme:product:added', cleanup, { once: true });
        document.addEventListener('theme:product:add-error', cleanup, { once: true });
        document.dispatchEvent(new CustomEvent('theme:cart:add', {
          bubbles: true,
          cancelable: true,
          detail: { button },
        }));
        return;
      }

      const originalText = button.textContent;
      button.disabled = true;
      button.textContent = 'LEGGER TIL';

      try {
        const response = await fetch(this.cartUrl('cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        if (!response.ok) throw new Error('cart/add failed');
        const item = await response.json();
        const cartResponse = await fetch(this.cartUrl('cart.js'), { headers: { Accept: 'application/json' } });
        const cart = cartResponse.ok ? await cartResponse.json() : null;

        this.refreshCartCount(cart?.item_count);
        this.announceCartUpdate(cart, item);
        this.openCartDrawer();

        button.textContent = 'LAGT TIL';
        const timer = window.setTimeout(() => {
          button.textContent = originalText;
          sync?.();
        }, 950);
        this.timers.push(timer);
      } catch (error) {
        button.textContent = 'PRØV IGJEN';
        const timer = window.setTimeout(() => {
          button.textContent = originalText;
          sync?.();
        }, 1400);
        this.timers.push(timer);
      }
    },

    refreshCartCount(count) {
      if (!Number.isFinite(Number(count))) return;
      const text = String(Number(count));
      document.querySelectorAll('[data-cart-count], [data-cart-count-bubble], .cart-count-bubble, .cart-count, .laley-hv2__cart-count').forEach((el) => {
        el.textContent = text;
        el.hidden = false;
        el.setAttribute('aria-hidden', 'false');
      });
    },

    announceCartUpdate(cart, item) {
      // cartCount MÅ med: temaets <cart-count>.onCartChange leser detail.cartCount
      // fra theme:cart:change og skriver bokstavelig «undefined» hvis den mangler.
      const detail = { cart, item, cartCount: Number(cart?.item_count) || 0 };
      ['cart:refresh', 'cart:updated', 'cart:change', 'theme:cart:change', 'theme:cart:updated'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      });
    },

    /* Packshot-kjeden (productLastImage/cartDrawerRoots/cartRowForAnchor/
       cartImageForRow/applyCartPackshotImage/bindCartPackshots/
       hydrateCartPackshots, ~180 linjer) er PENSJONERT (audit #7):
       cart-line-items.liquid server-rendrer packshoten (images | last, 1:1)
       for alle render-veier inkl. api-cart-items — JS-swappen degraderte den
       til ucappede full-res-bilder og kjørte ~80 DOM-pass per add. */

    openCartDrawer() {
      const detail = { source: 'pdp-outfit' };
      ['cart:open', 'theme:cart:open', 'ajaxCart:open', 'drawer:open'].forEach((name) => {
        document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
      });

      const drawer = document.querySelector('cart-drawer, [data-cart-drawer], #CartDrawer, .cart-drawer');
      if (drawer) {
        ['open', 'show', 'showDrawer'].some((method) => {
          if (typeof drawer[method] !== 'function') return false;
          drawer[method]();
          return true;
        });
        drawer.removeAttribute('hidden');
        drawer.classList.add('is-open', 'active', 'animate');
      }

      const toggle = document.querySelector('[data-cart-toggle], [data-cart-drawer-toggle], [aria-controls="CartDrawer"], [aria-controls="cart-drawer"]');
      if (toggle instanceof HTMLElement) {
        const timer = window.setTimeout(() => toggle.click(), 80);
        this.timers.push(timer);
      }
    },

    bindPurchaseOptions() {
      // Abonnementsvelger: synk selling_plan-input + ATC-pris med valgt
      // kjøpsalternativ. Rendres kun når produktet har selling plans.
      const wrap = this.section.querySelector('[data-pdp-purchase-options]');
      if (!wrap) return;
      const input = wrap.querySelector('[data-pdp-selling-plan-input]');
      const radios = Array.from(wrap.querySelectorAll('[data-pdp-purchase-radio]'));
      if (!input || !radios.length) return;
      const atcPrice = this.section.querySelector('[data-pdp-atc-price]');
      const sync = () => {
        const chosen = radios.find((radio) => radio.checked);
        if (!chosen) return;
        input.value = chosen.value || '';
        wrap.querySelectorAll('[data-pdp-purchase-option]').forEach((label) => {
          label.classList.toggle('is-selected', label.contains(chosen));
        });
        const cents = Number(chosen.dataset.planPrice);
        if (atcPrice && Number.isFinite(cents) && cents > 0) atcPrice.textContent = this.money(cents);
      };
      radios.forEach((radio) => this.on(radio, 'change', sync));
      // Fremtidssikring: plan-prisene er server-rendret for current_variant.
      // Ved variantbytte på flervariants-produkter med planer resettes valget
      // til engangskjøp (riktig pris garantert) i stedet for å vise stale
      // abonnementspris. Dagens abonnementsprodukter har én variant.
      if (this.variants.length > 1) {
        this.section.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
          this.on(input, 'change', () => {
            const onetime = radios.find((radio) => !radio.value);
            if (onetime && !onetime.checked) {
              onetime.checked = true;
              sync();
            }
          });
        });
      }
      sync();
    },

    bindForm() {
      if (!this.form || !this.atc) return;
      // Router basert på data-mode:
      //   "add"  → dispatch theme:cart:add (Laley-temaet håndterer AJAX + drawer)
      //   "bis"  → click Klaviyos BIS-trigger (eller fallback: åpne email-modal)
      //   andre → no-op
      this.on(this.form, 'submit', (event) => {
        event.preventDefault();
        this.clearError();
        const mode = this.atc.dataset.mode;
        if (mode === 'pick') {
          // CP-stil: klikk på ATC før str er valgt → vis inline-feil + scroll/fokuser picker.
          // ATC har allerede "Legg i handlekurv — X kr"-tekst, så feilmeldingen forklarer hva som mangler.
          this.showError('Velg størrelse for å fortsette');
          const dropdowns = this.section.querySelectorAll('[data-pdp-size-group] [data-pdp-dropdown]');
          dropdowns.forEach((d) => { d.open = true; });
          const sharedPicker = this.section.querySelector('[data-pdp-bundle-shared]:not([hidden])');
          const sizeGroup = this.section.querySelector('[data-pdp-size-group]')
            || this.optionGroups.find((group) => !group.querySelector('.pdp-clean__option--swatch'));
          const target = sharedPicker || sizeGroup || this.optionGroups[0];
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const focusEl = target.querySelector('button, .pdp-clean__dropdown-trigger, [data-pdp-option-input]');
            if (focusEl) focusEl.focus();
          }
          return;
        }
        if (mode === 'add') {
          // Dobbeltklikk-vern: uten guard gir to raske tap/submits to
          // theme:cart:add → qty 2 i kurven. Frigjøres når temaet melder
          // theme:product:added/add-error, med timeout som sikkerhetsnett.
          if (this.atc.dataset.pdpAdding === 'true') return;
          this.atc.dataset.pdpAdding = 'true';
          this.atc.setAttribute('aria-busy', 'true');
          let releaseTimer = 0;
          const release = () => {
            delete this.atc.dataset.pdpAdding;
            this.atc.removeAttribute('aria-busy');
            document.removeEventListener('theme:product:added', release);
            document.removeEventListener('theme:product:add-error', release);
            window.clearTimeout(releaseTimer);
          };
          releaseTimer = window.setTimeout(release, 4000);
          this.timers.push(releaseTimer);
          document.addEventListener('theme:product:added', release, { once: true });
          document.addEventListener('theme:product:add-error', release, { once: true });
          document.dispatchEvent(new CustomEvent('theme:cart:add', {
            detail: { button: this.atc },
            bubbles: false,
          }));
        } else if (mode === 'bis') {
          // Klaviyo injekter sin egen trigger i form eller andre steder.
          // Vi klikker den hvis vi finner den — Klaviyo åpner sin BIS-modal.
          const trigger = document.querySelector('.klaviyo-bis-trigger');
          if (trigger) {
            trigger.click();
          } else {
            // Fallback: dispatch event som tema/admin kan lytte på
            document.dispatchEvent(new CustomEvent('theme:product:bis-request', {
              detail: { variant: this.currentVariant, productId: this.section.dataset.productId },
              bubbles: true,
            }));
            // Som siste fallback: hjelp brukeren til kontakt
            const variantLabel = this.cleanVariantLabel(this.currentVariant);
            const subjectTitle = variantLabel ? `${this.productTitle()} - ${variantLabel}` : this.productTitle();
            const subject = encodeURIComponent(`Si fra når ${subjectTitle} er tilbake`);
            window.location.href = `mailto:hei@laley.no?subject=${subject}`;
          }
        }
      });
    },

    bindOutfitAdds() {
      // Hver outfit-knapp er nå type="submit" inni sin egen mini-form med
      // hidden id+quantity. Submit går direkte til cart/add.js uten modal.
      this.section.querySelectorAll('[data-pdp-outfit-form]').forEach((form) => {
        this.bindOutfitForm(form, false);
      });
    },

    clearError() {
      if (!this.error) return;
      this.error.textContent = '';
      this.error.hidden = true;
    },

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    },
  });
})();
